import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Radio,
  RadioGroup
} from '@mui/material';
import { Message, Send, CheckCircle, Error, Schedule, Science } from '@mui/icons-material';
import axios from 'axios';

interface WhatsAppTestProps {
  onClose?: () => void;
}

interface TestResult {
  timestamp: string;
  phoneNumber: string;
  message: string;
  status: 'success' | 'failed' | 'pending';
  error?: string;
  response?: any;
}

const WhatsAppTest: React.FC<WhatsAppTestProps> = ({ onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState('+1234567890');
  const [customMessage, setCustomMessage] = useState('¡Hola! Este es un mensaje de prueba del sistema ReservAr 🐱');
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [error, setError] = useState('');
  const [testType, setTestType] = useState<'normal' | 'direct'>('normal');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      case 'pending': return <Schedule color="warning" />;
      default: return <Message />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const handleTestWhatsApp = async (message?: string) => {
    const testMessage = message || customMessage;
    const testPhone = phoneNumber;

    if (!testPhone || !testMessage) {
      setError('Por favor completa el teléfono y el mensaje');
      return;
    }

    setLoading(true);
    setError('');

    const testResult: TestResult = {
      timestamp: new Date().toLocaleString('es-ES'),
      phoneNumber: testPhone,
      message: testMessage,
      status: 'pending'
    };

    setTestResults(prev => [testResult, ...prev]);

    try {
      console.log('🧪 Testing WhatsApp integration...');
      console.log('📱 Phone:', testPhone);
      console.log('💬 Message:', testMessage);
      console.log('🔧 Test type:', testType);

      const endpoint = testType === 'direct' ? '/api/whatsapp/test-webhook' : '/api/whatsapp/test';

      const response = await axios.post(endpoint, {
        phoneNumber: testPhone,
        message: testMessage
      });

      console.log('✅ WhatsApp test response:', response.data);

      // Update the test result with success
      setTestResults(prev => prev.map(result =>
        result.timestamp === testResult.timestamp
          ? {
              ...result,
              status: 'success',
              response: response.data,
              message: `${result.message} [${testType === 'direct' ? 'Directo' : 'Normal'}]`
            }
          : result
      ));

    } catch (err: any) {
      console.error('❌ WhatsApp test failed:', err);

      // Update the test result with error
      setTestResults(prev => prev.map(result =>
        result.timestamp === testResult.timestamp
          ? {
              ...result,
              status: 'failed',
              error: err.response?.data?.error || err.message
            }
          : result
      ));

      setError(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const predefinedTests = [
    {
      name: 'Confirmación de Reserva',
      message: '¡Hola! Tu reserva ha sido CONFIRMADA. ¡Te esperamos!'
    },
    {
      name: 'Cancelación de Reserva',
      message: 'Hola, lamentamos informarte que tu reserva ha sido CANCELADA.'
    },
    {
      name: 'Recordatorio de Turno',
      message: '¡Hola! Te recordamos que tienes un turno programado para mañana.'
    }
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Message color="primary" />
          Prueba de Integración WhatsApp
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Usa esta herramienta para probar si la integración de WhatsApp está funcionando correctamente.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Número de Teléfono"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+1234567890"
            fullWidth
          />

          <TextField
            label="Mensaje Personalizado"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Tipo de Prueba:
            </Typography>
            <RadioGroup
              row
              value={testType}
              onChange={(e) => setTestType(e.target.value as 'normal' | 'direct')}
            >
              <FormControlLabel
                value="normal"
                control={<Radio />}
                label="Normal (usa la función del sistema)"
              />
              <FormControlLabel
                value="direct"
                control={<Radio />}
                label="Directo (llama webhook directamente)"
              />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              {testType === 'normal'
                ? 'Prueba completa incluyendo logs y manejo de errores'
                : 'Prueba directa del webhook para verificar token y conectividad'
              }
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Send />}
              onClick={() => handleTestWhatsApp()}
              disabled={loading}
            >
              Enviar Mensaje Personalizado
            </Button>

            {onClose && (
              <Button variant="outlined" onClick={onClose}>
                Cerrar
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Predefined Tests */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Pruebas Predefinidas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Prueba los tipos de mensajes más comunes:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {predefinedTests.map((test, index) => (
            <Card key={index} variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {test.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {test.message}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleTestWhatsApp(test.message)}
                    disabled={loading}
                  >
                    Probar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>

      {/* Test Results */}
      {testResults.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Resultados de las Pruebas
          </Typography>

          <List>
            {testResults.map((result, index) => (
              <ListItem key={index} divider>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  {getStatusIcon(result.status)}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2">
                        {result.phoneNumber}
                      </Typography>
                      <Chip
                        label={result.status}
                        size="small"
                        color={getStatusColor(result.status) as any}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {result.timestamp}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                      "{result.message}"
                    </Typography>
                    {result.error && (
                      <Typography variant="caption" color="error">
                        Error: {result.error}
                      </Typography>
                    )}
                    {result.response && result.status === 'success' && (
                      <Typography variant="caption" color="success.main">
                        ✅ Mensaje enviado exitosamente
                      </Typography>
                    )}
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default WhatsAppTest;